<template>
  <Teleport :to="to">
    <Transition name="pm-backdrop-transition" enter-active-class="fadeInDown" leave-active-class="fadeOutUp">
      <div
        v-if="show"
        class="fixed left-0 top-0 z-[9999] flex w-full justify-center"
        aria-live="polite"
        aria-busy="true"
      >
        <div
          :style="{ width: `${width}px`, height: `${height}px` }"
          class="overflow-hidden rounded-b border-x border-b border-p-gray-30 bg-p-gray-10 shadow-sm transition-all duration-300"
        >
          <Transition enter-from-class="opacity-0" enter-active-class="transition duration-500">
            <Component :is="content" v-if="isComponent(content)" v-bind="componentProps" />
            <div v-else :class="textDivClass"><SdfBusyIndicator size="sm" class="h-4 pt-0.5" /> {{ content }}</div>
          </Transition>
        </div>
      </div>
    </Transition>
    <div v-if="content" class="invisible fixed">
      <div ref="dimsReference">
        <Component :is="content" v-if="isComponent(content)" v-bind="componentProps" />
        <div v-else :class="textDivClass"><SdfBusyIndicator size="sm" class="h-4 pt-0.5" /> {{ content }}</div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import SdfBusyIndicator from '@pequity/shared/ui/sdf-busy-indicator/sdf-busy-indicator.vue';
import { useWLoading } from '@pequity/shared/ui/w-loading/useWLoading';
import { isComponent } from '@pequity/shared/utils/component';
import { onBeforeUnmount, ref, toValue, watch } from 'vue';

/**
 * A loading component that displays a loading rectangle at the top center of the screen.
 * Shows a customizable content area with smooth animations and dynamic sizing.
 * Supports both text content and component content with automatic dimension calculation.
 *
 * @displayName WLoading
 */
defineOptions({
  name: 'WLoading',
});

withDefaults(defineProps<{ to?: string | HTMLElement }>(), {
  to: 'body',
});

const textDivClass = `flex h-8 justify-center gap-x-1.5 overflow-hidden whitespace-nowrap px-6 pt-1.5 text-sm font-semibold`;

const { show, content, props: componentProps, loadingHide } = useWLoading();
const dimsReference = ref<HTMLElement | null>(null);
const width = ref(0);
const height = ref(0);

watch(
  () => toValue(content),
  () => {
    if (dimsReference.value) {
      width.value = dimsReference.value.offsetWidth;
      height.value = dimsReference.value.offsetHeight;
    }
  },
  { flush: 'post' }
);

onBeforeUnmount(() => {
  loadingHide();
});
</script>

<style scoped lang="scss">
.fadeInDown {
  animation-duration: 0.4s;
  animation-fill-mode: both;
  animation-name: fadeInDown;
}

@keyframes fadeInDown {
  0% {
    opacity: 0;
    transform: translate3d(0, -100%, 0);
  }
  100% {
    opacity: 1;
    transform: none;
  }
}

.fadeOutUp {
  animation-duration: 0.25s;
  animation-fill-mode: both;
  animation-name: fadeOutUp;
}

@keyframes fadeOutUp {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translate3d(0, -100%, 0);
  }
}
</style>
